import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/20/solid';
import StickyProgressRow from './StickyProgressRow';

interface LearnerModuleHeaderNextProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** When true, renders the green "finish" gradient instead of purple. */
  finish?: boolean;
}

export interface BackOption {
  label: string;
  to: string;
}

interface LearnerModuleHeaderProps {
  /** Where the "Go back" pill points to (single destination). Required
   *  for backwards-compat; also acts as the default when `backOptions` is
   *  provided but the dropdown isn't desired. */
  backTo: string;
  backLabel?: string;
  /** When provided AND showBack, render a dropdown menu so the learner
   *  can choose between multiple back destinations (e.g. "Go back to
   *  modules" / "Go back to sections"). */
  backOptions?: BackOption[];
  /** 0–100. Coerced and clamped. */
  percent: number;
  /** Section title rendered above the progress bar. */
  sectionTitle: string;
  /** Set false to hide the back pill (e.g. on evaluation pages where the
   *  learner can't escape mid-attempt). Defaults to true. */
  showBack?: boolean;
  /** Next button rendered inline on the same row as Back + progress bar.
   *  Optional — omit on evaluation/feedback pages where navigation lives
   *  on Previous/Next pills below the question card instead. */
  next?: LearnerModuleHeaderNextProps;
}

function Spinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface BackDropdownProps {
  label: string;
  options: BackOption[];
}

const BackDropdown: React.FC<BackDropdownProps> = ({ label, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-secondary text-secondary text-sm font-bold hover:bg-secondary/10 active:scale-[0.98] transition-[background-color,transform]"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        {label}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+6px)] z-30 min-w-[220px] rounded-xl border border-border-secondary bg-surface-primary shadow-[0_12px_28px_-8px_rgba(15,15,28,0.18),0_4px_10px_-4px_rgba(15,15,28,0.08)] py-1"
        >
          {options.map((opt) => (
            <Link
              key={opt.to}
              to={opt.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 text-text-tertiary" />
              {opt.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Header rendered at the top of every learner module view (mode='learner').
 *
 * Layout:
 *   Row 1: Section title (sits above the action row).
 *   Row 2: "Go back" outlined orange pill | progress bar with chip | "Next
 *          Content" gradient pill — all on a single row per Figma
 *          9153:97269 / 9153:97276.
 *
 * On narrow viewports row 2 wraps so each control gets its own line; the
 * progress bar stays full-width via `flex-1 basis-full` on small screens.
 */
const LearnerModuleHeader: React.FC<LearnerModuleHeaderProps> = ({
  backTo,
  backLabel = 'Go back',
  backOptions,
  percent,
  sectionTitle,
  showBack = true,
  next,
}) => {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const useDropdown = !!backOptions && backOptions.length > 0;

  return (
    <>
      <h1 className="text-xl sm:text-2xl font-extrabold text-text-primary leading-tight mb-4">
        {sectionTitle}
      </h1>

      {/* The action row (back / progress / next) stays pinned while the
          module content scrolls — the title above scrolls away normally. */}
      <StickyProgressRow>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 sm:gap-x-12 lg:gap-x-16">
        {showBack &&
          (useDropdown ? (
            <BackDropdown label={backLabel} options={backOptions!} />
          ) : (
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-secondary text-secondary text-sm font-bold hover:bg-secondary/10 active:scale-[0.98] transition-[background-color,transform] shrink-0"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {backLabel}
            </Link>
          ))}

        {/* Progress bar — fills the row gap between the two pills.
            `pr-[29px]` reserves space for the half of the 58 px chip that
            overhangs past the bar's right edge (`translate-x-1/2` on the
            chip), preventing horizontal overflow on narrow viewports. */}
        <div
          className="relative flex-1 min-w-[180px] basis-full sm:basis-0 h-[58px] flex items-center pr-[29px]"
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="relative w-full h-2.5 rounded-full bg-neutral-200/70 dark:bg-white/25 shadow-[0_0_19.8px_rgba(0,0,0,0.12)]">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary-500 transition-[width] duration-500 ease-out"
              style={{ width: `${clamped}%` }}
            />
            <div className="pointer-events-none absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 size-[58px] flex items-center justify-center rounded-full bg-neutral-200/80 dark:bg-white/20 drop-shadow-[0_0_9.9px_rgba(0,0,0,0.12)] text-text-primary dark:text-white text-[13px] font-semibold">
              {clamped}%
            </div>
          </div>
        </div>

        {next && (
          <button
            type="button"
            onClick={next.onClick}
            disabled={next.disabled || next.loading}
            aria-busy={next.loading}
            className={`inline-flex items-center justify-center gap-2 min-w-[151px] h-9 px-4 rounded-full text-sm font-bold shrink-0 active:scale-[0.97] transition-[background-color,transform,opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-60 hover:brightness-110 shadow-[0_2px_8px_rgba(105,72,170,0.25)] ${ next.finish ? 'bg-gradient-to-t from-success-600 to-success-500' : 'bg-gradient-to-t from-primary-700 to-primary-500' }`}
          >
            {next.loading ? (
              <>
                <Spinner />
                Saving…
              </>
            ) : (
              <>
                {next.finish ? <CheckCircleIcon className="w-4 h-4" /> : null}
                {next.label}
                {!next.finish ? <ArrowRightIcon className="w-4 h-4" /> : null}
              </>
            )}
          </button>
        )}
      </div>
      </StickyProgressRow>
    </>
  );
};

export default LearnerModuleHeader;
